"use client"

import { useState, useEffect } from "react"
import { BottomNav } from "@/components/bottom-nav"
import { AnalyzeScreen } from "@/components/analyze-screen"
import { ProfileScreen } from "@/components/profile-screen"
import { UpgradeScreen } from "@/components/upgrade-screen"
import { EnhancedSplashScreen } from "@/components/enhanced-splash-screen"
import { LoginScreen } from "@/components/auth/login-screen"
import { SignupScreen } from "@/components/auth/signup-screen"
import PaymentScreen from "@/components/payment-screen"
import { AuthProvider, useAuth } from "@/components/auth/auth-context"
import { AuthPromptModal } from "@/components/auth/auth-prompt-modal"

require('dotenv').config({ path: '.env.local' });

function AppContent() {
  const [activeScreen, setActiveScreen] = useState("splash")
  const [previousScreen, setPreviousScreen] = useState<string | null>(null)
  const [showSplash, setShowSplash] = useState(true)
  const [showAuthPrompt, setShowAuthPrompt] = useState(false)
  const [authPromptReason, setAuthPromptReason] = useState<"save" | "premium" | "history">("save")
  const { user, isLoading, isAnonymous, setIsAnonymous } = useAuth()
  const [billingPlan, setBillingPlan] = useState<"monthly" | "yearly">("yearly")

  useEffect(() => {
    // Show splash screen for 3.5 seconds
    const timer = setTimeout(() => {
      setShowSplash(false)
      setActiveScreen((prev) => (prev === "splash" ? "analyze" : prev))

      // If no user is logged in, set to anonymous mode
      if (!user) {
        setIsAnonymous(true)
      }
    }, 3500)

    return () => clearTimeout(timer)
  }, [user, setIsAnonymous])

  // If still loading auth state or showing splash, show splash screen
  if (isLoading || showSplash) {
    return <EnhancedSplashScreen />
  }

  const handleProtectedAction = (reason: "save" | "premium" | "history") => {
    if (!user && isAnonymous) {
      setAuthPromptReason(reason)
      setShowAuthPrompt(true)
      return false
    }
    return true
  }

  const handleScreenChange = (screen: string, plan?: "monthly" | "yearly") => {
    // Save previous screen for back navigation
    if (
      screen !== activeScreen &&
      !["login", "signup"].includes(screen) &&
      !["login", "signup"].includes(activeScreen)
    ) {
      setPreviousScreen(activeScreen)
    }

    if (plan) {
      setBillingPlan(plan)
    }

    // Check if the screen requires authentication
    // Note: We're allowing "upgrade" to be accessible without authentication
    if (["profile", "payment"].includes(screen) && !user && isAnonymous) {
      const reason = screen === "payment" ? "premium" : "history"
      handleProtectedAction(reason)
    } else {
      setActiveScreen(screen)
    }
  }

  const handleBackNavigation = () => {
    if (previousScreen) {
      setActiveScreen(previousScreen)
      setPreviousScreen(null)
    } else {
      setActiveScreen("analyze")
    }
  }

  return (
    <div className="flex flex-col h-screen bg-background text-foreground overflow-hidden">
      <main className="flex-1 overflow-auto pb-28">
        {activeScreen === "analyze" && (
          <AnalyzeScreen setActiveScreen={handleScreenChange} onSaveResult={() => handleProtectedAction("save")} />
        )}
        {activeScreen === "profile" && <ProfileScreen setActiveScreen={handleScreenChange} initialTab="profile" />}
        {activeScreen === "history" && <ProfileScreen setActiveScreen={handleScreenChange} initialTab="history" />}
        
        {activeScreen === "upgrade" && <UpgradeScreen setActiveScreen={handleScreenChange} />}
        {activeScreen === "payment" && <PaymentScreen setActiveScreen={handleScreenChange} billingPlan={billingPlan} />}
        {activeScreen === "login" && (
          <LoginScreen setActiveScreen={handleScreenChange} onBackClick={handleBackNavigation} />
        )}
        {activeScreen === "signup" && (
          <SignupScreen setActiveScreen={handleScreenChange} onBackClick={handleBackNavigation} />
        )}
      </main>

      {["analyze", "profile", "upgrade"].includes(activeScreen) && (
        <div className="fixed bottom-0 left-0 right-0 h-20 z-50">
          <BottomNav activeScreen={activeScreen} setActiveScreen={handleScreenChange} />
        </div>
      )}

      <AuthPromptModal
        isOpen={showAuthPrompt}
        onClose={() => setShowAuthPrompt(false)}
        onLogin={() => {
          setShowAuthPrompt(false)
          setActiveScreen("login")
        }}
        onSignup={() => {
          setShowAuthPrompt(false)
          setActiveScreen("signup")
        }}
        reason={authPromptReason}
        returnToScreen={activeScreen}
      />
    </div>
  )
}

export default function Home() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  )
}

